import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { nonEmptyString } from '@atlassian/amkt-common';
import { makeAppKey } from '@atlassian/mpac-api-client';
import { makeInstalledAppFromSPIInstalledApp, makeMarketplaceAppDisplayDetailsFromMarketplaceApp, makePermissionCheckResultFromSPI } from '@atlassian/mpac-spi';
import { Either, Option } from 'funfix-core';
import { List } from 'immutable';
import { makeInstallableAppFromSPIInstallableAddonAndMarketplaceAppData } from '../../../records/InstallableApp';
import { foldInstallableAppIdentifier, makeInstallableAppByAriIdentifier, makeInstallableAppByKeyIdentifier, toSPIInstallableAddonIdentifier } from '../../../records/InstallableAppIdentifier';
import { makeDeterminingInstallMode } from '../../../records/InstallMode/DeterminingInstallMode';
import { makeInstallableAppDefaultInstallMode } from '../../../records/InstallMode/InstallableAppDefaultInstallMode';
import { makePendingConfirmationInstallStatus } from '../../../records/InstallMode/InstallStatus';
import { makeInstructionalInstallMode } from '../../../records/InstallMode/InstructionalInstallMode';
import { makeInstallInstructionsStep, makeInstructionalApp } from '../../../records/InstructionalApp';
import getSPI from '../../../services/SPI';
import { reflect } from '../../../utils/promise-utils';
import { getRequestId } from '../../../utils/request-utils';
import { retrieveAppDataByAri, retrieveAppDataByKey, retrieveAppDataByUrl } from '../AppDataRetrieval';
import { doSetInstallMode } from '../Installation';
export const FINISH_DETERMINING_INSTALL_MODE = 'INSTALLATION_FINISH_DETERMINING_INSTALL_MODE';
export const DETERMINING_INSTALL_MODE_ERROR = 'INSTALLATION_DETERMINING_INSTALL_MODE_ERROR';
const finishDeterminingInstallMode = _ref => {
  let {
    installMode,
    retrievalId
  } = _ref;
  return {
    type: FINISH_DETERMINING_INSTALL_MODE,
    installMode,
    retrievalId
  };
};
const determiningInstallModeError = _ref2 => {
  let {
    error,
    installableAppId,
    retrievalId
  } = _ref2;
  return {
    type: DETERMINING_INSTALL_MODE_ERROR,
    error,
    installableAppId,
    retrievalId
  };
};
const attemptInstructionalAppInstallMode = _ref3 => {
  var _marketplaceApp$_embe;
  let {
    mpacBaseUrl,
    marketplaceApp
  } = _ref3;
  return Option.of((_marketplaceApp$_embe = marketplaceApp._embedded) === null || _marketplaceApp$_embe === void 0 ? void 0 : _marketplaceApp$_embe.version).map(v => {
    var _v$_embedded, _v$vendorLinks, _v$vendorLinks2;
    return v.deployment.connect ? Either.left('Instructional apps cannot be connect apps') : Either.right(makeInstructionalInstallMode({
      instructionalApp: makeInstructionalApp({
        details: makeMarketplaceAppDisplayDetailsFromMarketplaceApp({
          marketplaceApp,
          mpacBaseUrl
        }),
        steps: List((_v$_embedded = v._embedded) === null || _v$_embedded === void 0 ? void 0 : _v$_embedded.instructions).map(step => {
          var _step$_embedded;
          return makeInstallInstructionsStep({
            image: (_step$_embedded = step._embedded) === null || _step$_embedded === void 0 ? void 0 : _step$_embedded.image._links.image.href,
            bodyHTML: step.body
          });
        }),
        learnMoreLink: nonEmptyString(((_v$vendorLinks = v.vendorLinks) === null || _v$vendorLinks === void 0 ? void 0 : _v$vendorLinks.learnMore) || ((_v$vendorLinks2 = v.vendorLinks) === null || _v$vendorLinks2 === void 0 ? void 0 : _v$vendorLinks2.binary))
      })
    }));
  }).getOrElse(Either.left('Cannot resolve data from Marketplace response'));
};
const attemptInstallableAppForDefaultInstallMode = _ref4 => {
  let {
    installableAddon,
    installableAppId,
    installedAddon,
    installPermission,
    marketplaceApp,
    mpacBaseUrl,
    relayState
  } = _ref4;
  return Either.map2(installableAddon.fold(error => Either.left("InstallableApp install mode needs a successful installableAddon resolved: ".concat(error)), r => Either.right(r)), installPermission.fold(error => Either.left("InstallableApp install mode needs a successful installPermission resolved: ".concat(error)), r => Either.right(r)), (ia, p) => makeInstallableAppDefaultInstallMode({
    installableApp: makeInstallableAppFromSPIInstallableAddonAndMarketplaceAppData(ia, marketplaceApp, mpacBaseUrl),
    installPermission: makePermissionCheckResultFromSPI(p),
    installedApp: installedAddon.toOption().map(makeInstalledAppFromSPIInstalledApp),
    installableAppId,
    relayState,
    status: makePendingConfirmationInstallStatus()
  }));
};
export const parseInstalledAddonResult = installedAddonResult => {
  if (installedAddonResult.status && installedAddonResult.status === 'NOT_FOUND') {
    throw new Error("Failed to fetch app details while determining install mode");
  }
  const installedAddon = installedAddonResult;
  return installedAddon;
};
const handleAriDetermineMode = (mpacBaseUrl, installableAppByARI, relayState, context, flags) => Promise.all([retrieveAppDataByAri({
  installableAppByARI,
  mpacBaseUrl
}), reflect(getSPI().getInstalledAddon({
  contextId: context.id,
  addon: toSPIInstallableAddonIdentifier(makeInstallableAppByAriIdentifier({
    ari: makeAppKey(installableAppByARI.ari),
    appKey: makeAppKey(installableAppByARI.appKey)
  })),
  flags: _objectSpread(_objectSpread({}, flags), {}, {
    retryForLicenseDetails: false
  })
}).then(installedAddonResult => parseInstalledAddonResult(installedAddonResult)))]).then(_ref5 => {
  let [{
    installableAddon,
    installPermission,
    marketplaceApp
  }, installedAddon] = _ref5;
  return attemptInstallableAppForDefaultInstallMode({
    installableAddon,
    installableAppId: installableAppByARI,
    installedAddon,
    installPermission,
    marketplaceApp,
    mpacBaseUrl,
    relayState
  }).map(Either.right).getOrElseL(() => attemptInstructionalAppInstallMode({
    mpacBaseUrl,
    marketplaceApp
  }));
}).catch(error => Either.left(error));
export const getInstallMode = _ref6 => {
  let {
    mpacBaseUrl,
    installableAppId,
    relayState,
    context,
    flags,
    hostCmtEnabled = false
  } = _ref6;
  const installMode = foldInstallableAppIdentifier(installableAppId)(installableAppByKeyId => Promise.all([retrieveAppDataByKey({
    installableAppByKeyId,
    mpacBaseUrl
  }, hostCmtEnabled), reflect(getSPI().getInstalledAddon({
    contextId: context.id,
    addon: toSPIInstallableAddonIdentifier(makeInstallableAppByKeyIdentifier({
      key: makeAppKey(installableAppByKeyId.key)
    }))
  }).then(installedAddonResult => parseInstalledAddonResult(installedAddonResult)))]).then(_ref7 => {
    let [{
      installableAddon,
      installPermission,
      marketplaceApp
    }, installedAddon] = _ref7;
    return attemptInstallableAppForDefaultInstallMode({
      installableAddon,
      installableAppId: installableAppByKeyId,
      installedAddon,
      installPermission,
      marketplaceApp,
      mpacBaseUrl,
      relayState
    }).map(Either.right).getOrElseL(() => attemptInstructionalAppInstallMode({
      mpacBaseUrl,
      marketplaceApp
    }));
  }).catch(error => Either.left(error)), installableAppByUrlId => retrieveAppDataByUrl({
    installableAppByUrlId,
    mpacBaseUrl
  }).then(_ref8 => {
    let {
      installableAddon,
      installPermission,
      marketplaceApp
    } = _ref8;
    const installedAddonPromise = installableAddon.fold(() => Promise.resolve(Either.left('No installableAddon')), ia => reflect(getSPI().getInstalledAddon({
      contextId: context.id,
      addon: toSPIInstallableAddonIdentifier(makeInstallableAppByKeyIdentifier({
        key: makeAppKey(ia.details.key)
      }))
    }).then(installedAddonResult => parseInstalledAddonResult(installedAddonResult))));
    return installedAddonPromise.then(installedAddon => attemptInstallableAppForDefaultInstallMode({
      installableAddon,
      installPermission,
      installableAppId: installableAppByUrlId,
      installedAddon,
      marketplaceApp,
      mpacBaseUrl,
      relayState
    }));
  }).catch(error => Either.left(error)), installableAppByARI => handleAriDetermineMode(mpacBaseUrl, installableAppByARI, relayState, context, flags));
  return installMode;
};
export const doInitiateInstallModeForIdentifier = _ref9 => {
  let {
    mpacBaseUrl,
    installableAppId,
    relayState,
    context
  } = _ref9;
  return dispatch => {
    const retrievalId = getRequestId();
    const flags = {
      enableReactivateForgeAppFromEmcee: false
    };
    dispatch(doSetInstallMode({
      installMode: makeDeterminingInstallMode({
        retrievalId
      })
    }));
    return getInstallMode({
      mpacBaseUrl,
      installableAppId,
      relayState,
      context,
      flags
    }).then(result => result.fold(error => {
      console.error("Either the SPI's getInstallableAddon returned an error or invalid shape, " + 'or MPAC returned an invalid instructional listing: ', error, "installableAppId=".concat(installableAppId.toString()));
      dispatch(determiningInstallModeError({
        error,
        installableAppId,
        retrievalId
      }));
    }, installMode => {
      dispatch(finishDeterminingInstallMode({
        installMode,
        retrievalId
      }));
    }));
  };
};